import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, Download, Lock, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABEL = {
  player: "Player",
  club: "Football Community / Club",
  academy: "Academy",
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "player", label: "Players" },
  { id: "club", label: "Communities / Clubs" },
  { id: "academy", label: "Academies" },
];

const inputCls =
  "w-full rounded-md border border-[#2A2A2A] bg-[#0B0B0B] px-4 py-3 font-body text-base text-white placeholder-[#B3B3B3]/40 outline-none transition-colors duration-300 focus:border-[#1F7A4D]";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso || "";
  }
}

function displayName(r) {
  return r.role === "player" ? r.name || "" : r.org_name || "";
}

function details(r) {
  if (r.role === "player") {
    return [r.position, r.preferred_foot, r.age ? `Age ${r.age}` : null]
      .filter(Boolean)
      .join(" · ");
  }
  if (r.role === "club") {
    return [
      r.number_of_players ? `${r.number_of_players} players` : null,
      r.number_of_teams ? `${r.number_of_teams} teams` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  return [
    r.number_of_players ? `${r.number_of_players} players` : null,
    (r.age_groups || []).length ? (r.age_groups || []).join(", ") : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function toCSV(rows) {
  const headers = [
    "Role",
    "Name",
    "Representative",
    "Email",
    "Mobile",
    "Location",
    "Registered",
    "Position",
    "PreferredFoot",
    "Age",
    "Players",
    "Teams",
    "AgeGroups",
  ];
  const esc = (v) => {
    const s = v === undefined || v === null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(",")];
  rows.forEach((r) => {
    lines.push(
      [
        ROLE_LABEL[r.role] || r.role,
        displayName(r),
        r.representative_name || "",
        r.email || "",
        r.mobile || "",
        r.location || "",
        fmtDate(r.created_at),
        r.position || "",
        r.preferred_foot || "",
        r.age || "",
        r.number_of_players || "",
        r.number_of_teams || "",
        (r.age_groups || []).join(" | "),
      ]
        .map(esc)
        .join(","),
    );
  });
  return lines.join("\n");
}

export default function AdminRegistrations() {
  const [secret, setSecret] = useState("");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const login = async (e) => {
    e?.preventDefault?.();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/registrations`, {
        headers: { "X-Admin-Secret": secret },
      });
      if (res.status === 401) {
        setError("Access denied. Invalid admin secret.");
        setLoading(false);
        return;
      }
      const body = await res.json();
      setItems(body.items || []);
      setAuthed(true);
    } catch {
      setError("Unable to reach the server. Please try again.");
    }
    setLoading(false);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((r) => {
      if (filter !== "all" && r.role !== filter) return false;
      if (!q) return true;
      return [
        r.name,
        r.org_name,
        r.representative_name,
        r.email,
        r.mobile,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [items, query, filter]);

  const exportCSV = () => {
    const blob = new Blob([toCSV(filtered)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tfc-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* -------- Access gate -------- */
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] text-white flex items-center justify-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-gate"
          className="w-full max-w-[420px] border border-[#2A2A2A] bg-[#151515] p-8"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#2A2A2A] text-[#1F7A4D]">
            <Lock size={18} />
          </span>
          <h1 className="mt-6 font-display uppercase text-4xl leading-none">
            Admin Access
          </h1>
          <p className="mt-3 font-body text-sm text-[#B3B3B3]">
            Enter the admin secret to view registrations.
          </p>
          <input
            type="password"
            className={`${inputCls} mt-6`}
            data-testid="admin-secret-input"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="Admin secret"
            autoFocus
          />
          {error && (
            <p
              data-testid="admin-error"
              className="mt-4 border-l-2 border-[#1F7A4D] bg-[#0B0B0B] px-4 py-2.5 font-body text-sm text-white"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-enter"
            className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#1F7A4D] px-6 py-3.5 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-[#26945D] disabled:opacity-60"
          >
            {loading ? "Checking..." : "Enter"}
          </button>
          <Link
            to="/"
            className="mt-5 inline-flex items-center gap-2 font-body text-xs text-[#B3B3B3] transition-colors hover:text-[#26945D]"
          >
            <ArrowLeft size={14} /> Back to the Collective
          </Link>
        </form>
      </div>
    );
  }

  /* -------- Data view -------- */
  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12 py-12 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="flex items-center gap-3">
              <span className="h-2 w-2 rounded-full bg-[#1F7A4D]" />
              <span className="font-body text-xs font-medium tracking-[0.3em] uppercase text-[#B3B3B3]">
                Admin
              </span>
            </span>
            <h1 className="mt-4 font-display uppercase text-4xl md:text-5xl leading-none">
              Registrations
            </h1>
          </div>
          <span
            data-testid="admin-count"
            className="font-body text-sm text-[#B3B3B3]"
          >
            {filtered.length} of {items.length} shown
          </span>
        </div>

        {/* Toolbar */}
        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-[380px]">
            <Search
              size={16}
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#B3B3B3]/60"
            />
            <input
              className={`${inputCls} pl-10`}
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, mobile, club or academy"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`admin-filter-${f.id}`}
                className={`rounded-full border px-4 py-2 font-body text-sm transition-colors duration-300 ${
                  filter === f.id
                    ? "border-[#1F7A4D] bg-[#1F7A4D]/10 text-white"
                    : "border-[#2A2A2A] text-[#B3B3B3] hover:border-[#1F7A4D]"
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              onClick={exportCSV}
              data-testid="admin-export"
              className="inline-flex items-center gap-2 rounded-full bg-[#1F7A4D] px-4 py-2 font-body text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#26945D]"
            >
              <Download size={15} /> Export CSV
            </button>
          </div>
        </div>

        {/* Desktop table */}
        <div className="mt-8 hidden overflow-x-auto border border-[#2A2A2A] md:block">
          <table className="w-full border-collapse" data-testid="admin-table">
            <thead>
              <tr className="border-b border-[#2A2A2A] text-left font-body text-[0.68rem] uppercase tracking-[0.14em] text-[#B3B3B3]/70">
                <th className="px-5 py-3.5 font-medium">Role</th>
                <th className="px-5 py-3.5 font-medium">Name</th>
                <th className="px-5 py-3.5 font-medium">Contact</th>
                <th className="px-5 py-3.5 font-medium">Location</th>
                <th className="px-5 py-3.5 font-medium">Registered</th>
                <th className="px-5 py-3.5 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-[#2A2A2A]/60 font-body text-sm text-[#B3B3B3]"
                >
                  <td className="px-5 py-4">
                    <span className="whitespace-nowrap text-[0.7rem] uppercase tracking-[0.12em] text-[#1F7A4D]">
                      {ROLE_LABEL[r.role]}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-white">{displayName(r)}</span>
                    {r.representative_name && (
                      <span className="block text-xs text-[#B3B3B3]/70">
                        {r.representative_name}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className="block text-white/90">{r.email}</span>
                    <span className="block text-xs text-[#B3B3B3]/70">
                      {r.mobile}
                    </span>
                  </td>
                  <td className="px-5 py-4">{r.location}</td>
                  <td className="px-5 py-4 whitespace-nowrap">
                    {fmtDate(r.created_at)}
                  </td>
                  <td className="px-5 py-4">{details(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <p className="px-5 py-8 text-center font-body text-sm text-[#B3B3B3]">
              No registrations match your search.
            </p>
          )}
        </div>

        {/* Mobile cards */}
        <div className="mt-8 flex flex-col gap-4 md:hidden">
          {filtered.map((r) => (
            <div
              key={r.id}
              data-testid="admin-card"
              className="border border-[#2A2A2A] bg-[#151515] p-5"
            >
              <div className="flex items-center justify-between">
                <span className="text-[0.68rem] uppercase tracking-[0.12em] text-[#1F7A4D]">
                  {ROLE_LABEL[r.role]}
                </span>
                <span className="font-body text-xs text-[#B3B3B3]/70">
                  {fmtDate(r.created_at)}
                </span>
              </div>
              <p className="mt-3 font-display uppercase text-2xl leading-none text-white">
                {displayName(r)}
              </p>
              {r.representative_name && (
                <p className="mt-1 font-body text-xs text-[#B3B3B3]/70">
                  {r.representative_name}
                </p>
              )}
              <div className="mt-3 space-y-1 font-body text-sm text-[#B3B3B3]">
                <p className="text-white/90">{r.email}</p>
                <p>{r.mobile}</p>
                <p>{r.location}</p>
                {details(r) && <p className="text-[#B3B3B3]">{details(r)}</p>}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="py-8 text-center font-body text-sm text-[#B3B3B3]">
              No registrations match your search.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
